import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Truck, CheckCircle2, Package, FileText, ShoppingBag, Briefcase, Eye, AlertCircle, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";

const TABS = [
    { key: "retail", label: "Retail orders", icon: ShoppingBag, color: "sky" },
    { key: "b2b", label: "B2B orders", icon: Briefcase, color: "indigo" },
    { key: "slips", label: "Packing slips", icon: FileText, color: "emerald" },
];

export default function PackingPortal() {
    const [tab, setTab] = useState("retail");
    const [orders, setOrders] = useState([]);
    const [slips, setSlips] = useState({ rows: [], totals: { count: 0, retail: 0, b2b: 0 }, scope: { scope: "all" } });
    const [active, setActive] = useState(null);
    const [packing, setPacking] = useState({});
    const [busy, setBusy] = useState(false);
    const [counters, setCounters] = useState({ retail: 0, b2b: 0, slips: 0 });

    // ---- loaders ----
    const loadQueue = async (orderType) => {
        const { data } = await api.get("/packing/queue", { params: { order_type: orderType } });
        setOrders(data);
        const map = {};
        data.forEach(o => {
            map[o.id] = (o.items || []).map((it, i) => ({
                idx: i,
                packed_quantity: o.packing?.items?.[i]?.packed_quantity ?? it.quantity,
                available: o.packing?.items?.[i]?.available ?? true,
                note: o.packing?.items?.[i]?.note || "",
            }));
        });
        setPacking(map);
    };

    const loadSlips = async () => {
        const { data } = await api.get("/packing/slips");
        setSlips(data);
    };

    const loadCounters = async () => {
        try {
            const [retail, b2b, slipResp] = await Promise.all([
                api.get("/packing/queue", { params: { order_type: "retail" } }),
                api.get("/packing/queue", { params: { order_type: "b2b" } }),
                api.get("/packing/slips"),
            ]);
            setCounters({ retail: retail.data.length, b2b: b2b.data.length, slips: slipResp.data.totals.count });
        } catch (e) {
            console.error("Packing counters failed to load:", e);
        }
    };

    useEffect(() => {
        loadCounters();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; loadCounters is stable
    }, []);

    useEffect(() => {
        setActive(null);
        if (tab === "slips") {
            loadSlips();
        } else {
            loadQueue(tab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only the tab switch should refetch
    }, [tab]);

    // ---- mutations ----
    const setLine = (orderId, idx, patch) => setPacking(p => ({
        ...p, [orderId]: p[orderId].map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));

    const save = async (orderId) => {
        setBusy(true);
        try {
            await api.put(`/packing/orders/${orderId}/items`, packing[orderId]);
            toast.success("Progress saved");
            await loadQueue(tab);
            await loadCounters();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };

    const finalize = async (orderId) => {
        if (!window.confirm("Finalise this packing? Stock will be auto-deducted, and the slip will appear in the Packing slips tab for billing.")) return;
        setBusy(true);
        try {
            await api.put(`/packing/orders/${orderId}/items`, packing[orderId]);
            await api.post(`/packing/orders/${orderId}/finalize`);
            toast.success("Packed → slip generated");
            setActive(null);
            await loadQueue(tab);
            await loadCounters();
        } catch (e) { toast.error(e.response?.data?.detail || "Finalise failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="packing-portal" className="space-y-5">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Warehouse</div>
                <h1 className="font-serif text-4xl text-stone-900">Packing & Dispatch</h1>
                <p className="text-sm text-stone-500 mt-1">Process retail and B2B orders. Slips become visible to Admin, Editor, Accounts &amp; Billing, Packing &amp; Dispatch, and MR (territory-scoped) once finalised.</p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 border-b border-stone-200" data-testid="packing-tabs">
                {TABS.map((t) => {
                    const TIcon = t.icon;
                    const n = counters[t.key] || 0;
                    const isActive = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`tab-${t.key}`}
                            className={`px-4 py-3 -mb-px flex items-center gap-2 text-sm border-b-2 transition-colors ${
                                isActive
                                    ? "border-sky-700 text-sky-700 font-semibold bg-white"
                                    : "border-transparent text-stone-500 hover:text-stone-900 hover:bg-stone-50"
                            }`}
                        >
                            <TIcon className="w-4 h-4" />
                            {t.label}
                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${isActive ? "bg-sky-100 text-sky-700" : "bg-stone-200 text-stone-600"}`}>{n}</span>
                        </button>
                    );
                })}
            </div>

            {/* ---- QUEUE TABS (retail / b2b) ---- */}
            {tab !== "slips" && (
                <div className="space-y-3" data-testid={`queue-${tab}`}>
                    {orders.length === 0 ? (
                        <div className="bg-white border border-stone-200 p-10 text-center text-sm text-stone-500">
                            <Package className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                            No {tab === "retail" ? "retail" : "B2B"} orders waiting in the queue.
                        </div>
                    ) : orders.map(o => {
                        const open = active === o.id;
                        const oboName = o.on_behalf_of?.name;
                        return (
                            <div key={o.id} className="bg-white border border-stone-200" data-testid={`pack-order-${o.id}`}>
                                <button onClick={() => setActive(open ? null : o.id)} className="w-full p-4 flex items-center gap-4 text-left">
                                    <div className="flex-1">
                                        <div className="font-medium flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5">#{o.id.slice(0, 8)}</span>
                                            <span>{oboName || o.user_name || "—"}</span>
                                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${o.order_type === "retail" ? "bg-sky-100 text-sky-700" : "bg-indigo-100 text-indigo-700"}`}>{o.order_type}</span>
                                            {o.on_behalf_of?.customer_type && o.on_behalf_of.customer_type !== "retailer" && (
                                                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-stone-100 text-stone-600">{o.on_behalf_of.customer_type}</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
                                            <span>{o.items?.length} items</span>
                                            <span>·</span>
                                            <span className="font-medium text-stone-700">{formatINR(o.total)}</span>
                                            <span>·</span>
                                            <span>{o.created_at?.slice(0, 10)}</span>
                                            {(o.address?.city || o.address?.state) && (
                                                <>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[o.address?.city, o.address?.state].filter(Boolean).join(", ")}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-xs uppercase tracking-widest">
                                        {o.packing?.status === "in_progress"
                                            ? <span className="text-amber-700">In progress</span>
                                            : <span className="text-stone-500">Pending</span>}
                                    </div>
                                </button>

                                {open && (
                                    <div className="border-t border-stone-200 p-4 space-y-3" data-testid={`pack-detail-${o.id}`}>
                                        {/* Address card */}
                                        <div className="bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600 grid sm:grid-cols-2 gap-2">
                                            <div>
                                                <div className="font-medium text-stone-700 mb-1">Ship to</div>
                                                <div className="font-medium text-stone-900">{o.address?.name}</div>
                                                {o.address?.phone && <div>{o.address.phone}</div>}
                                                <div>{[o.address?.line1, o.address?.line2].filter(Boolean).join(", ")}</div>
                                                <div>{[o.address?.city, o.address?.state, o.address?.pincode].filter(Boolean).join(", ")}</div>
                                            </div>
                                            {oboName && (
                                                <div>
                                                    <div className="font-medium text-stone-700 mb-1">Placed on behalf of</div>
                                                    <div className="font-medium text-stone-900">{oboName}</div>
                                                    {o.on_behalf_of?.phone && <div>{o.on_behalf_of.phone}</div>}
                                                    <div className="text-stone-500 mt-1">By: {o.user_name} ({o.user_role})</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Items */}
                                        {o.items.map((it, idx) => {
                                            const pi = packing[o.id]?.[idx] || { packed_quantity: it.quantity, available: true };
                                            return (
                                                <div key={`${it.product_id}-${it.size_id}-${idx}`} className={`grid grid-cols-12 gap-2 items-center border p-3 ${pi.available ? "border-stone-200 bg-white" : "border-red-200 bg-red-50/40"}`} data-testid={`pack-line-${o.id}-${idx}`}>
                                                    <div className="col-span-5">
                                                        <div className={`text-sm ${pi.available ? "" : "line-through text-stone-500"}`}>{it.product_name}</div>
                                                        <div className="text-xs text-stone-500">{it.size_label} · ordered: <span className="font-medium">{it.quantity}</span> · {formatINR(it.price)} each</div>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label className="text-[10px] uppercase tracking-widest text-stone-500">Packed qty</label>
                                                        <Input
                                                            type="number"
                                                            min={0}
                                                            max={it.quantity}
                                                            value={pi.packed_quantity}
                                                            onChange={(e) => setLine(o.id, idx, { packed_quantity: Math.max(0, parseInt(e.target.value || 0, 10) || 0) })}
                                                            className="rounded-none border-stone-300 h-9"
                                                            disabled={!pi.available}
                                                            data-testid={`pack-qty-${o.id}-${idx}`}
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
                                                            <Switch checked={pi.available} onCheckedChange={(v) => setLine(o.id, idx, { available: v, packed_quantity: v ? it.quantity : 0 })} data-testid={`pack-avail-${o.id}-${idx}`} />
                                                            <span className={`text-[11px] ${pi.available ? "text-emerald-700" : "text-red-700"}`}>{pi.available ? "Available" : "Out of stock"}</span>
                                                        </label>
                                                    </div>
                                                    <div className="col-span-3">
                                                        <label className="text-[10px] uppercase tracking-widest text-stone-500">Note</label>
                                                        <Input
                                                            value={pi.note || ""}
                                                            onChange={(e) => setLine(o.id, idx, { note: e.target.value })}
                                                            placeholder={pi.available ? "Optional" : "Why is it OOS?"}
                                                            className="rounded-none border-stone-300 h-9"
                                                            data-testid={`pack-note-${o.id}-${idx}`}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Action bar */}
                                        <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200">
                                            <Button onClick={() => save(o.id)} disabled={busy} variant="outline" className="rounded-none border-stone-400" data-testid={`pack-save-${o.id}`}>
                                                <Package className="w-4 h-4 mr-1" />Save progress
                                            </Button>
                                            <Button onClick={() => finalize(o.id)} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid={`pack-finalize-${o.id}`}>
                                                <Truck className="w-4 h-4 mr-1" />Finalise &amp; generate slip
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ---- SLIPS TAB ---- */}
            {tab === "slips" && (
                <div className="space-y-3" data-testid="slips-tab">
                    {slips.scope?.scope === "mr_territory" && (
                        <div className="bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-900 flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5" />
                            <div>
                                Showing slips for orders shipping to your territory: <strong>{slips.scope.mr_states.join(", ") || "(none assigned)"}</strong>.
                                {slips.scope.mr_states.length === 0 && " Ask your admin to assign at least one state."}
                            </div>
                        </div>
                    )}
                    {slips.rows.length === 0 ? (
                        <div className="bg-white border border-stone-200 p-10 text-center text-sm text-stone-500">
                            <FileText className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                            No packing slips yet{slips.scope?.scope === "mr_territory" ? " in your territory" : ""}.
                        </div>
                    ) : (
                        <div className="bg-white border border-stone-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50 border-b border-stone-200">
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Order</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Type</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Ships to</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Packed / Ordered</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">OOS</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Total</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Packed at</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Billing</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Slip</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slips.rows.map((r) => (
                                        <tr key={r.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`slip-row-${r.id}`}>
                                            <td className="p-3 font-mono text-xs">#{r.order_short}</td>
                                            <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.is_b2b ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"}`}>{r.order_type}</span></td>
                                            <td className="p-3">
                                                <div className="font-medium text-stone-900">{r.customer_name}</div>
                                                {r.customer_phone && <div className="text-[10px] text-stone-500">{r.customer_phone}</div>}
                                            </td>
                                            <td className="p-3 text-xs text-stone-600">
                                                {r.city ? <span>{r.city}, </span> : null}<span className="font-medium">{r.state || "—"}</span>
                                            </td>
                                            <td className="p-3 text-right font-mono">{r.items_packed_qty} / {r.items_ordered_qty}</td>
                                            <td className="p-3 text-right">
                                                {r.oos_lines > 0
                                                    ? <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><AlertCircle className="w-3 h-3" />{r.oos_lines}</span>
                                                    : <span className="text-stone-400">—</span>}
                                            </td>
                                            <td className="p-3 text-right font-mono text-emerald-700">{formatINR(r.total)}</td>
                                            <td className="p-3 text-[11px] text-stone-500">{r.packed_at?.slice(0, 16).replace("T", " ")}<div className="text-[10px] text-stone-400">by {r.packed_by_name || "—"}</div></td>
                                            <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.billing_status === "paid" ? "bg-emerald-100 text-emerald-800" : r.billing_status === "invoiced" ? "bg-sky-100 text-sky-700" : "bg-stone-100 text-stone-600"}`}>{r.billing_status}</span></td>
                                            <td className="p-3 text-right">
                                                <Link to={`/employee/packing/slip/${r.id}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline text-xs" data-testid={`slip-view-${r.id}`}>
                                                    <Eye className="w-3.5 h-3.5" />View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
